// ============================================================================
// ConsultantQaPanel.jsx
// AI follow-up Q&A panel rendered at the bottom of each consultant tab.
//
// Props
//   tabId          – e.g. 'executive', 'improvements' (used by getSuggestedQuestions)
//   tabLabel       – human label shown in the panel header
//   themeColor     – accent colour matching the parent tab
//   resultData     – the parsed AI JSON result for this tab (or null if not yet generated)
//   generated      – boolean: true once the tab has been generated
//   modelTitle     – process model title string
//   processLabel   – e.g. 'Process'
//   entityLabel    – e.g. 'Work Item'
//   aiModelConfig  – { provider, model, temperature, label }
// ============================================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getAIModelConfig, getInvokeAIProvider } from '@advisor-core/lib/aiModelConfig';
import { invokeWebAI } from '@advisor-core/lib/webAiClient';
import {
  buildConsultantQaContext,
  buildConsultantQaPrompt,
  classifyConsultantQuestion,
  getSuggestedQuestionsForTab,
} from '@process-advisor/process-miner/consultant/consultantQaEngine';
import { getAppVersion } from '@process-advisor/process-miner/processMiner/processMiner';
import { invokeProcessOpenAIWithBilling } from '@process-advisor/process-miner/consultant/aiBillingAccess';

// ── Forge lazy loader ─────────────────────────────────────────────────────────
let _forgeModule = null;
const loadForge = async () => {
  if (!_forgeModule) _forgeModule = await import('@forge/bridge');
  return _forgeModule;
};

// ── JSON cleaner (mirrors pattern used by all other tabs) ─────────────────────
const cleanAndParseJSON = (raw) => {
  let s = (raw || '{}')
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/```\s*$/i, '')
    .trim();
  const lastBrace = s.lastIndexOf('}');
  if (lastBrace !== -1) s = s.substring(0, lastBrace + 1);
  try { return JSON.parse(s); } catch (_) {}
  const opens = [];
  for (const ch of s) {
    if (ch === '{' || ch === '[') opens.push(ch);
    else if (ch === '}' || ch === ']') opens.pop();
  }
  let recovered = s.replace(/,?\s*"[^"]*"?\s*:?\s*[^,\}\]]*$/, '');
  for (let i = opens.length - 1; i >= 0; i--) {
    recovered += opens[i] === '{' ? '}' : ']';
  }
  try { return JSON.parse(recovered); } catch (_) { return {}; }
};


const getDirectGeminiKey = () => {
  try {
    if (typeof API_KEY !== 'undefined' && API_KEY) return API_KEY;
  } catch (_) {}
  return undefined;
};

const callDirectGeminiForQa = async ({ prompt, model }) => {
  if (getAppVersion() === 'WEB') {
    const result = await invokeWebAI({ prompt, reportLabel: 'Ask AI', responseFormat: 'json' });
    return cleanAndParseJSON(result?.text || '{}');
  }
  const configuredModel = getAIModelConfig().model;
  const geminiModel = model && String(model).startsWith('gemini') ? model : configuredModel;
  const key = getDirectGeminiKey();
  const res = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${geminiModel}:generateContent?key=${key}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: `${prompt}\n\nReturn valid JSON only. Do not wrap the response in markdown fences.` }] }],
        generationConfig: { responseMimeType: 'application/json', maxOutputTokens: 32768 },
      }),
    }
  );

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Gemini QA request failed: ${res.status} ${res.statusText} ${body}`);
  }

  const data = await res.json();
  const raw = data?.candidates?.[0]?.content?.parts?.[0]?.text || '{}';
  return cleanAndParseJSON(raw);
};

// ── Priority badge ────────────────────────────────────────────────────────────
const PriorityBadge = ({ priority }) => {
  const colors = {
    Now:   { bg: '#FFEBE6', text: '#DE350B' },
    Next:  { bg: '#FFF0B3', text: '#974F0C' },
    Later: { bg: '#E6F1FB', text: '#0C66E4' },
  };
  const c = colors[priority] || colors.Later;
  return (
    <span style={{
      flexShrink: 0,
      padding: '2px 7px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 700,
      background: c.bg,
      color: c.text,
    }}>
      {priority || 'Later'}
    </span>
  );
};

// ── Animated loading dots ─────────────────────────────────────────────────────
const LoadingDots = ({ color }) => (
  <>
    <style>{`
      @keyframes qaBounceDot {
        0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
        40% { transform: scale(1); opacity: 1; }
      }
    `}</style>
    <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {[0, 0.2, 0.4].map((delay, i) => (
        <span key={i} style={{
          width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block',
          animation: `qaBounceDot 1.2s ${delay}s infinite ease-in-out`,
        }} />
      ))}
    </span>
  </>
);

// ── Rendered answer body ──────────────────────────────────────────────────────
const AnswerContent = ({ answer, themeColor, onFollowUp }) => {
  const { answer_summary, key_points = [], recommended_actions = [], limits = [], follow_up_questions = [] } = answer;
  return (
    <div style={{ fontSize: 13, color: '#172B4D', lineHeight: 1.6 }}>
      {answer_summary && (
        <p style={{ margin: '0 0 12px', fontWeight: 500 }}>{answer_summary}</p>
      )}
      {key_points.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: themeColor, marginBottom: 6 }}>Key Points</div>
          {key_points.map((kp, i) => (
            <div key={i} style={{ marginBottom: 8, paddingLeft: 12, borderLeft: `3px solid ${themeColor}` }}>
              {kp.label && <div style={{ fontWeight: 600, marginBottom: 2 }}>{kp.label}</div>}
              {kp.detail && <div style={{ color: '#42526E' }}>{kp.detail}</div>}
            </div>
          ))}
        </div>
      )}
      {recommended_actions.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: themeColor, marginBottom: 6 }}>Recommended Actions</div>
          {recommended_actions.map((ra, i) => (
            <div key={i} style={{ marginBottom: 8, padding: '8px 12px', background: '#F4F5F7', borderRadius: 6, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <PriorityBadge priority={ra.priority} />
              <div style={{ flex: 1 }}>
                {ra.action && <div style={{ fontWeight: 600, marginBottom: 2 }}>{ra.action}</div>}
                {ra.why && <div style={{ color: '#42526E', fontSize: 12 }}>{ra.why}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      {limits.length > 0 && (
        <div style={{ marginBottom: 8, padding: '8px 12px', background: '#FFFAE6', borderRadius: 6, border: '1px solid #FFE380' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#974F0C', marginBottom: 4 }}>Limitations</div>
          {limits.map((l, i) => <div key={i} style={{ fontSize: 12, color: '#5E6C84', marginBottom: 2 }}>• {l}</div>)}
        </div>
      )}
      {follow_up_questions.filter(Boolean).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#5E6C84', marginBottom: 6 }}>You might also ask:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {follow_up_questions.filter(Boolean).map((q, i) => (
              <button key={i} onClick={() => onFollowUp(q)} style={{
                background: 'transparent', border: `1px solid ${themeColor}`, borderRadius: 20,
                padding: '4px 10px', fontSize: 12, color: themeColor, cursor: 'pointer', fontWeight: 500,
              }}>{q}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Single conversation turn ──────────────────────────────────────────────────
const AnswerBlock = ({ turn, themeColor, onFollowUp }) => (
  <div style={{ marginBottom: 16, borderRadius: 8, border: '1px solid #DFE1E6', overflow: 'hidden', background: '#fff' }}>
    <div style={{ padding: '10px 14px', background: '#F4F5F7', borderBottom: '1px solid #DFE1E6', fontSize: 13, fontWeight: 600, color: '#172B4D', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: '50%', background: themeColor, color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>Q</span>
      <span>{turn.question}</span>
    </div>
    <div style={{ padding: '12px 14px' }}>
      {turn.isLoading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#5E6C84', fontSize: 13 }}>
          <LoadingDots color={themeColor} /><span>Consulting AI…</span>
        </div>
      )}
      {turn.isError && <div style={{ color: '#DE350B', fontSize: 13 }}>Could not get a response. Please try again.</div>}
      {!turn.isLoading && !turn.isError && turn.answer && (
        <AnswerContent answer={turn.answer} themeColor={themeColor} onFollowUp={onFollowUp} />
      )}
    </div>
  </div>
);

// ── Extract plain text from saved HTML for fallback context ──────────────────
const extractTextFromHtml = (html) => {
  if (!html) return '';
  try {
    const div = document.createElement('div');
    div.innerHTML = html
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '');
    // Walk text nodes, join with newlines, collapse whitespace
    const text = (div.textContent || div.innerText || '')
      .replace(/\s+/g, ' ')
      .trim();
    return text.slice(0, 8000); // cap to avoid prompt overflow
  } catch (_) {
    return '';
  }
};

// ── Main component ────────────────────────────────────────────────────────────
export default function ConsultantQaPanel({
  tabId,
  tabLabel,
  themeColor = '#0052CC',
  resultData,
  savedHtml,
  generated,
  modelTitle,
  processLabel = 'Process',
  entityLabel = 'Work Item',
  aiModelConfig,
  // Side-panel control — passed from the tab header button
  isOpen,
  onClose,
}) {
  const [question, setQuestion] = useState('');
  const [turns, setTurns]       = useState([]);
  const [isAsking, setIsAsking] = useState(false);
  const pollRef                 = useRef(null);
  const textareaRef             = useRef(null);
  const scrollRef               = useRef(null);

  const suggestedQuestions = getSuggestedQuestionsForTab(tabId);

  // Auto-scroll to latest answer
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns]);

  // Focus textarea when panel opens
  useEffect(() => {
    if (isOpen) setTimeout(() => textareaRef.current?.focus(), 80);
  }, [isOpen]);

  // Cleanup poll on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleFollowUp = useCallback((q) => {
    setQuestion(q);
    setTimeout(() => textareaRef.current?.focus(), 50);
  }, []);

  const askQuestion = useCallback(async (q) => {
    if (!q.trim() || isAsking) return;
    const trimmedQ = q.trim();
    setQuestion('');
    setIsAsking(true);

    const priorTurns = turns
      .filter(t => t.answer)
      .map(t => ({ question: t.question, answerSummary: t.answer?.answer_summary || '' }));

    setTurns(prev => [...prev, { question: trimmedQ, answer: null, isLoading: true, isError: false }]);

    const questionType = classifyConsultantQuestion(trimmedQ);
    let prompt;
    if (resultData && Object.keys(resultData).length > 0) {
      const context = buildConsultantQaContext({ tabId, tabLabel, resultData, modelTitle: modelTitle || '', processLabel: processLabel || 'Process', entityLabel: entityLabel || 'Work Item' });
      prompt = buildConsultantQaPrompt({ question: trimmedQ, questionType, context, priorTurns });
    } else {
      const htmlText = extractTextFromHtml(savedHtml || '');
      prompt = `You are an AI process intelligence consultant answering a question about a ${tabLabel} report.\n\nREPORT CONTENT\n${htmlText || 'No content available.'}\n\nQUESTION TYPE\n${questionType}\n\nRESPONSE FORMAT\nReturn exactly one JSON object:\n{\n  "answer_summary": "",\n  "key_points": [{ "label": "", "detail": "", "evidence_ids": [] }],\n  "recommended_actions": [{ "action": "", "why": "", "priority": "Now|Next|Later", "evidence_ids": [] }],\n  "limits": [""],\n  "follow_up_questions": ["", ""],\n  "evidence_used": []\n}\n\nUSER QUESTION\n${trimmedQ}`;
    }

    const cfg         = getAIModelConfig(aiModelConfig);
    const provider    = cfg.provider    || 'gemini';
    const model       = cfg.model;
    const temperature = cfg.temperature ?? 0.3;
    const toInvProv   = (p) => p === 'gemini' ? 'Gemini' : p === 'claude' ? 'Claude' : 'ChatGPT';

    const updateTurn = (patch) => {
      setTurns(prev => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].question === trimmedQ && updated[i].isLoading) {
            updated[i] = { ...updated[i], ...patch };
            break;
          }
        }
        return updated;
      });
    };

    try {
      const appVersion = typeof getAppVersion === 'function' ? getAppVersion() : 'FORGE';

      if (appVersion === 'SNOW' || appVersion === 'WEB') {
        const parsed = await callDirectGeminiForQa({ prompt, model });
        updateTurn({ isLoading: false, answer: parsed });
        setIsAsking(false);
        return;
      }

      const { invoke } = await loadForge();
      const response   = await invokeProcessOpenAIWithBilling({
        invoke,
        reportLabel: 'Ask AI',
        payload: { prompt, aiProvider: toInvProv(provider), model, temperature },
      });

      if (!response?.requestId) throw new Error('No requestId');
      const requestId = response.requestId;
      let pollCount   = 0;

      pollRef.current = setInterval(async () => {
        pollCount += 1;
        if (pollCount > 120) {
          clearInterval(pollRef.current);
          updateTurn({ isLoading: false, isError: true });
          setIsAsking(false);
          return;
        }
        try {
          const statusData = await invoke('checkStatus', { requestId });
          if (statusData?.status === 'completed') {
            clearInterval(pollRef.current);
            const parsed = cleanAndParseJSON(statusData.result || '{}');
            updateTurn({ isLoading: false, answer: parsed });
            setIsAsking(false);
          } else if (statusData?.status === 'failed') {
            clearInterval(pollRef.current);
            updateTurn({ isLoading: false, isError: true });
            setIsAsking(false);
          }
        } catch (err) {
          console.error('[ConsultantQaPanel] poll error:', err);
          clearInterval(pollRef.current);
          updateTurn({ isLoading: false, isError: true });
          setIsAsking(false);
        }
      }, 1000);

    } catch (err) {
      console.error('[ConsultantQaPanel] invoke error:', err);
      updateTurn({ isLoading: false, isError: true });
      setIsAsking(false);
    }
  }, [isAsking, turns, tabId, tabLabel, resultData, modelTitle, processLabel, entityLabel, aiModelConfig]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); askQuestion(question); }
    if (e.key === 'Escape') onClose?.();
  };

  if (!generated || (!resultData && !savedHtml)) return null;

  return (
    <>
      <style>{`@keyframes qaSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 1100,
          background: 'rgba(9,30,66,0.32)',
          display: isOpen ? 'block' : 'none',
        }}
      />

      {/* Side panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1101,
        width: 460,
        background: '#fff',
        boxShadow: '-4px 0 32px rgba(9,30,66,0.22)',
        display: 'flex', flexDirection: 'column',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}>

        {/* Panel header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px',
          background: 'linear-gradient(135deg, #1E3A5F 0%, #2C5282 100%)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Ask AI</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>
                {tabLabel} · {turns.length > 0 ? `${turns.length} question${turns.length !== 1 ? 's' : ''} this session` : 'Grounded in this analysis'}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', padding: 4, borderRadius: 4, display: 'flex', alignItems: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Conversation history */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>

          {/* Suggested questions (empty state) */}
          {turns.length === 0 && suggestedQuestions.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#5E6C84', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Suggested questions</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {suggestedQuestions.map((q, i) => (
                  <button key={i} onClick={() => handleFollowUp(q)}
                    style={{ background: '#F4F5F7', border: '1px solid #DFE1E6', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#172B4D', cursor: 'pointer', fontWeight: 500, textAlign: 'left', lineHeight: 1.4 }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = themeColor; e.currentTarget.style.background = '#E9F2FF'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#DFE1E6'; e.currentTarget.style.background = '#F4F5F7'; }}
                  >{q}</button>
                ))}
              </div>
            </div>
          )}

          {/* Conversation turns */}
          {turns.map((turn, i) => (
            <AnswerBlock key={i} turn={turn} themeColor={themeColor} onFollowUp={handleFollowUp} />
          ))}
        </div>

        {/* Input area */}
        <div style={{ padding: '12px 16px 16px', borderTop: '1px solid #EBECF0', background: '#FAFBFC', flexShrink: 0 }}>
          <textarea
            ref={textareaRef}
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Ask a question about this ${tabLabel}…`}
            disabled={isAsking}
            rows={3}
            style={{ width: '100%', resize: 'none', border: '1px solid #C1C7D0', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#172B4D', background: isAsking ? '#F4F5F7' : '#fff', boxSizing: 'border-box', outline: 'none', lineHeight: 1.5, fontFamily: 'inherit' }}
            onFocus={e => { if (!isAsking) e.target.style.borderColor = themeColor; }}
            onBlur={e => { e.target.style.borderColor = '#C1C7D0'; }}
          />
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#97A0AF' }}>⌘↵ to send · Esc to close</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {turns.length > 0 && (
                <button onClick={() => setTurns([])} disabled={isAsking}
                  style={{ background: 'transparent', border: '1px solid #DFE1E6', borderRadius: 6, padding: '6px 12px', fontSize: 12, color: '#5E6C84', cursor: isAsking ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
                  Clear
                </button>
              )}
              <button
                onClick={() => askQuestion(question)}
                disabled={isAsking || !question.trim()}
                style={{ background: isAsking || !question.trim() ? '#DFE1E6' : themeColor, border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 12, fontWeight: 700, color: isAsking || !question.trim() ? '#97A0AF' : '#fff', cursor: isAsking || !question.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {isAsking ? <><LoadingDots color="#97A0AF" />Asking…</> : <>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  Ask AI
                </>}
              </button>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}

// ── Ask AI header button — import this in each tab's header ──────────────────
export function AskAiButton({ onClick, hasContent = false, count = 0 }) {
  return (
    <button
      onClick={onClick}
      disabled={!hasContent}
      title={hasContent ? 'Ask AI a question about this analysis' : 'Generate content first to enable Ask AI'}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        padding: '6px 12px', borderRadius: 4,
        background: '#fff',
        border: hasContent ? '1px solid #0052CC' : '1px solid #DFE1E6',
        minHeight: 30,
        fontSize: 12, fontWeight: 700,
        color: hasContent ? '#0052CC' : '#97A0AF',
        cursor: hasContent ? 'pointer' : 'not-allowed',
        transition: 'background 0.15s, border-color 0.15s, color 0.15s',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => { if (hasContent) e.currentTarget.style.background = '#E9F2FF'; }}
      onMouseLeave={e => { if (hasContent) e.currentTarget.style.background = '#fff'; }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
      Ask AI
      {count > 0 && (
        <span style={{ background: '#0052CC', color: '#fff', borderRadius: 999, fontSize: 10, fontWeight: 800, padding: '1px 5px', minWidth: 16, textAlign: 'center' }}>
          {count}
        </span>
      )}
    </button>
  );
}


